/**
 * {paymentNotice}の作成画面 /payment-notices/:id（#233 3b、docs/93 §15.6）。見積書のエディタ（Estimate.tsx の Editor）を{order}と同じ形で使う
 *   上から 頭の帯（1段目＝‹・題・状態チップ・自動保存・↶ ↷・発行・⋯／2段目＝{vendor} 御中・発行日・No.・件名・{project} ›・元 {order} ›・合計）→
 *   条件の帯（相手の登録番号か「適格請求書発行事業者以外」と控除割合・確認の期限＝送付後 n日）→ 明細（社内用の列なし）→ 備考
 *   ・頭の自動保存は共通の仕組み（lib/saveQueue.tsx）。明細と同じ版を1本ずつ送る（409 PAYMENT_NOTICE_STALE＝「他の利用者が変更しました」）
 *   ・発行すると紙の画面（送付）へ。相手が確認ページで「確認」を押すか、送付後 n日を過ぎて連絡が無ければ確認済＝仕入が確定
 *   ・スマホ版と 767px 以下は閲覧のみ（作るのは PC。PC_ONLY_FEATURES の paymentNoticeCreate）
 */
import { PAYMENT_NOTICE_MSG, PURCHASE_MSG, creditRatioText, saveGateText, unissuedTitle, type Company, type GroupList, type LineInput, type PaymentNotice, type PaymentNoticeIssueResult, type SheetInput, type UnissueResult, unissuedReissue } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { ME_KEY, useMe } from "../../auth.js";
import { DeleteDraftDialog } from "../../components/DeleteDraftDialog.jsx";
import { namePageNow } from "../../components/BackButton.jsx";
import { docShort } from "../../components/AreaIcon.jsx";
import { DocField, subjWidth } from "../../components/DocHead.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { HeadActions, type HeadAction } from "../../components/HeadActions.jsx";
import { UnissueDialog } from "../../components/UnissueDialog.jsx";
import { ErrorText, Field, useToast } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { yen } from "../../lib/num.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { currentView } from "../../lib/view.js";
import { useSaveQueue } from "../../lib/saveQueue.jsx";
import { Editor, type InvoiceBackend } from "../Estimate.jsx";
import { NoticeStateChip } from "./parts.jsx";

type Head = { subject: string; issueDate: string; notes: string; groupId: string | null };
const headOf = (n: PaymentNotice): Head => ({ subject: n.revision.subject, issueDate: n.revision.issueDate, notes: n.revision.notes, groupId: n.projectGroup?.id ?? null });

export function PaymentNoticePage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["payment-notice", id], queryFn: () => api<PaymentNotice>("GET", `/payment-notices/${id}`), retry: false });
  if (q.isPending) return <p className="hint">読み込んでいます…</p>;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/payment-notices">{t(PAYMENT_NOTICE_MSG.list)}</Link>
        </p>
      </div>
    );
  }
  // 発行・取消しで作り直す
  return <NoticeEditor key={`${q.data.id}:${q.data.status}:${q.data.revision.id}`} initial={q.data} />;
}

/** 相手の登録番号の帯の文字（画面）：「登録番号 T…」／「適格請求書発行事業者以外（控除割合 70%（経過措置 ～2028/9））」 */
export function noticeVendorText(n: Pick<PaymentNotice, "vendorTax">): string {
  const v = n.vendorTax;
  if (v.qualified && v.registrationNo) return `${PAYMENT_NOTICE_MSG.vendorRegNo} ${v.registrationNo}`;
  const r = creditRatioText(v.creditRatio, v.creditUntil);
  return `${PAYMENT_NOTICE_MSG.vendorUnqualified}${r ? `（${PURCHASE_MSG.credit} ${r}）` : ""}`;
}

function NoticeEditor({ initial }: { initial: PaymentNotice }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const { toast, node: toastNode } = useToast();
  const narrow = useNarrow();
  const phone = narrow || currentView() === "phone";
  const readOnly = initial.status !== "draft" || phone;
  const title = initial.names.paymentNotice;
  const cur = (qc.getQueryData(["payment-notice", initial.id]) as PaymentNotice | undefined) ?? initial;
  const [head, setHead] = useState<Head>(() => headOf(initial));
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => api<GroupList>("GET", "/groups?state=all"), enabled: !readOnly });
  const groupChoices: Array<{ id: string; name: string }> = (groups.data?.items ?? []).filter((g) => !g.completedAt || g.id === head.groupId);
  if (cur.projectGroup && !groupChoices.some((g) => g.id === cur.projectGroup!.id)) groupChoices.unshift({ id: cur.projectGroup.id, name: cur.projectGroup.name });

  /* ---- 版：明細（Editor）と頭は同じ PUT を別々の列で送る。版は1つ・1本ずつ（{order}と同じ） ---- */
  const ver = useRef(initial.updatedAt);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const serial = <T,>(fn: () => Promise<T>): Promise<T> => {
    const p = chain.current.then(fn, fn);
    chain.current = p.catch(() => undefined);
    return p;
  };
  const staleRef = useRef(false);
  const openStale = useRef<() => void>(() => undefined);
  const headBase = useRef<Head>(headOf(initial));
  const latest = useRef(head);
  latest.current = head;
  const hq = useSaveQueue({
    send: async () => {
      const h = latest.current;
      const n = await serial(() => api<PaymentNotice>("PUT", `/payment-notices/${initial.id}`, { subject: h.subject, issueDate: h.issueDate, notes: h.notes, projectGroupId: h.groupId, version: ver.current }));
      ver.current = n.updatedAt;
      headBase.current = headOf(n);
      qc.setQueryData(["payment-notice", initial.id], n);
      void qc.invalidateQueries({ queryKey: ["payment-notices"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "PAYMENT_NOTICE_STALE") {
        staleRef.current = true;
        openStale.current();
        return { msg: PAYMENT_NOTICE_MSG.stale, then: "stop" };
      }
    },
    blocked: () => staleRef.current,
  });
  const setH = (patch: Partial<Head>, immediate = false) => {
    setHead((h) => ({ ...h, ...patch }));
    latest.current = { ...latest.current, ...patch };
    hq.mark(immediate);
  };
  const linesReady = useRef<() => Promise<string | null>>(async () => null);
  const register = useCallback((f: () => Promise<string | null>) => {
    linesReady.current = f;
  }, []);
  const readyAll = async (): Promise<string | null> => (await linesReady.current()) ?? (await hq.ready());

  /* ---- 発行 ---- */
  const needsCompany = !!me.data?.tenant.needsCompanyName;
  const [dlg, setDlg] = useState<{ company: string; busy: boolean; error?: string | undefined } | null>(null);
  const openIssue = async () => {
    const why = await readyAll();
    if (why != null) {
      toast(saveGateText("issue", why), undefined, "error");
      return;
    }
    let company = "";
    if (needsCompany) {
      try {
        company = (await api<Company>("GET", "/company")).name;
      } catch {
        /* 空のまま */
      }
    }
    setDlg({ company, busy: false });
  };
  const doIssue = async () => {
    if (!dlg) return;
    if (needsCompany && !dlg.company.trim()) {
      setDlg({ ...dlg, error: PAYMENT_NOTICE_MSG.companyRequired });
      return;
    }
    setDlg({ ...dlg, busy: true, error: undefined });
    try {
      const why = await readyAll();
      if (why != null) {
        setDlg({ ...dlg, busy: false, error: saveGateText("issue", why) });
        return;
      }
      const r = await api<PaymentNoticeIssueResult>("POST", `/payment-notices/${initial.id}/issue`, { ...(needsCompany ? { company: { name: dlg.company.trim() } } : {}), version: ver.current });
      setDlg(null);
      hq.discard();
      void qc.invalidateQueries({ queryKey: ME_KEY });
      void qc.invalidateQueries({ queryKey: ["payment-notices"] });
      await qc.invalidateQueries({ queryKey: ["payment-notice", initial.id] });
      // 発行したら紙の画面（送付）へ
      namePageNow(title, docShort(title, r.documentNo)); // 送信の画面の戻るボタンに番号（読み直しで編集の画面が作り直されても、移る前に付ける）
      nav(`/payment-notices/${initial.id}/paper`, { state: { notice: t(PAYMENT_NOTICE_MSG.issued).replace("{paymentNotice}", title).replace("{no}", r.documentNo), noticeTestId: "issued-notice" } });
    } catch (e) {
      setDlg((d) => (d ? { ...d, busy: false, error: messageOf(e) } : d));
    }
  };

  /* ---- 取消し・削除 ---- */
  const [askDelete, setAskDelete] = useState(false);
  const [unissueDlg, setUnissueDlg] = useState<{ busy: boolean; error?: string | undefined } | null>(null);
  const doUnissue = async () => {
    setUnissueDlg({ busy: true });
    try {
      const r = await api<UnissueResult>("POST", `/payment-notices/${initial.id}/unissue`, {});
      setUnissueDlg(null);
      toast(t(PAYMENT_NOTICE_MSG.unissued).replace("{paymentNotice}", title).replace("{no}", r.documentNo), undefined, "success", "unissued-notice");
      void qc.invalidateQueries({ queryKey: ["payment-notices"] });
      await qc.invalidateQueries({ queryKey: ["payment-notice", initial.id] });
    } catch (e) {
      setUnissueDlg({ busy: false, error: messageOf(e) });
    }
  };
  // 窓は components/DeleteDraftDialog（見積書と同じ形。docs/96 §13.5）＝断られたら窓の中に文が出る
  const deleteDraft = async () => {
    await api<void>("DELETE", `/payment-notices/${initial.id}`);
    hq.discard();
    void qc.invalidateQueries({ queryKey: ["payment-notices"] });
    setAskDelete(false);
    nav(initial.projectGroup ? `/groups/${initial.projectGroup.id}?tab=purchases` : "/payment-notices", { replace: true });
  };

  const actions: HeadAction[] = readOnly
    ? [
        { label: "PDF 表示", to: `/payment-notices/${initial.id}/paper`, testId: "notice-paper", rank: 1 },
        ...(initial.canUnissue ? [{ label: PAYMENT_NOTICE_MSG.unissue, onSelect: () => setUnissueDlg({ busy: false }), testId: "notice-unissue", group: "state" as const }] : []),
        ...(initial.purchase ? [{ label: PAYMENT_NOTICE_MSG.purchaseOpen, to: `/purchases/${initial.purchase.id}`, testId: "notice-purchase", group: "doc" as const }] : []),
      ]
    : [
        { label: PAYMENT_NOTICE_MSG.pdfDraft, onSelect: () => void readyAll().then((why) => (why == null ? nav(`/payment-notices/${initial.id}/paper`) : toast(saveGateText("pdf", why), undefined, "error"))), testId: "notice-pdf-draft", group: "doc" as const },
        { label: PAYMENT_NOTICE_MSG.deleteDraft, onSelect: () => setAskDelete(true), testId: "notice-delete", group: "danger" as const },
      ];

  const backend: InvoiceBackend = {
    doc: {
      kind: "payment_notice",
      title,
      queryKey: "payment-notice",
      listKey: "payment-notices",
      back: initial.projectGroup ? { to: `/groups/${initial.projectGroup.id}?tab=purchases`, label: initial.projectGroup.name } : { to: "/payment-notices", label: t(PAYMENT_NOTICE_MSG.list) },
      labor: false,
    },
    save: (body: { sheets: SheetInput[]; lines: LineInput[] }) =>
      serial(async () => {
        const n = await api<PaymentNotice>("PUT", `/payment-notices/${initial.id}`, { ...body, version: ver.current });
        ver.current = n.updatedAt;
        return n;
      }),
    version: () => ver.current,
    reload: () => api<PaymentNotice>("GET", `/payment-notices/${initial.id}`),
    stale: () => {
      staleRef.current = true;
    },
    registerStale: (o) => {
      openStale.current = o;
    },
    synced: (e) => {
      const theirs = headOf(e as PaymentNotice);
      const base = headBase.current;
      const mine = latest.current;
      const merged: Head = { ...theirs };
      for (const k of Object.keys(theirs) as Array<keyof Head>) {
        if (JSON.stringify(mine[k]) !== JSON.stringify(base[k])) (merged as Record<keyof Head, unknown>)[k] = mine[k];
      }
      ver.current = e.updatedAt;
      headBase.current = theirs;
      latest.current = merged;
      setHead(merged);
      staleRef.current = false;
      if (JSON.stringify(merged) !== JSON.stringify(theirs)) hq.mark(true);
      else {
        hq.discard();
        hq.setState((x) => (x.kind === "error" ? { kind: "idle" } : x));
      }
    },
    register,
    actions: null,
    // #245（docs/94 B-4 案B）：頭は DocHead の3段。3段目＝宛先・発行日・No.・{project}・元
    docHead: {
      kind: "purchases",
      kindLabel: title,
      title: (
        <input id="pn_subj" className="dh-subj" style={subjWidth(head.subject)} aria-label={PAYMENT_NOTICE_MSG.colSubject} placeholder={PAYMENT_NOTICE_MSG.colSubject} value={head.subject} readOnly={readOnly} onChange={(e) => setH({ subject: e.target.value })} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} data-testid="notice-subject" />
      ),
      primary: (
        <span className="hd-acts" data-testid="head-actions">
          {readOnly ? null : (
            <button className="btn primary" type="button" onClick={() => void openIssue()} data-testid="notice-issue">
              {PAYMENT_NOTICE_MSG.issue}
            </button>
          )}
          <HeadActions actions={actions} label={t(PAYMENT_NOTICE_MSG.stateMenu).replace("{paymentNotice}", title)} part="main" />
        </span>
      ),
      menu: <HeadActions actions={actions} label={t(PAYMENT_NOTICE_MSG.stateMenu).replace("{paymentNotice}", title)} part="more" />,
      meta: (
        <>
          <DocField label="宛先">
            <span className="dh-to">
              <b data-testid="notice-vendor">{initial.client.name}</b> <span className="hon">{initial.client.honorific || "御中"}</span>
            </span>
          </DocField>
          <DocField label="発行日">
            <DateInput inline label="発行日" ariaLabel="発行日" value={head.issueDate} readOnly={readOnly} clearable={false} onChange={(v) => v && setH({ issueDate: v }, true)} testId="notice-issue-date" />
          </DocField>
          <DocField label="No.">
            <span className="num" data-testid="notice-no">{initial.displayNo ?? initial.documentNo ?? "未発行"}</span>
          </DocField>
          <DocField label={<span id="pngroup-l">{t("{project}")}</span>}>
            {readOnly ? (
              cur.projectGroup ? <Link to={`/groups/${cur.projectGroup.id}`} data-testid="notice-group">{cur.projectGroup.name} ›</Link> : <span className="muted">—</span>
            ) : (
              <select className="inp ogroupsel" aria-labelledby="pngroup-l" value={head.groupId ?? ""} onChange={(e) => setH({ groupId: e.target.value || null }, true)} data-testid="notice-group-select">
                <option value="">—</option>
                {groupChoices.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            )}
          </DocField>
          {cur.sourceOrder ? (
            <DocField label={PAYMENT_NOTICE_MSG.source}>
              <Link to={`/orders/${cur.sourceOrder.id}/paper`} data-testid="notice-source-order">{`${cur.names.order} ${cur.sourceOrder.displayNo ?? ""}`.trim()} ›</Link>
            </DocField>
          ) : null}
        </>
      ),
    },
    chips: <NoticeStateChip state={cur.state} waitingDays={cur.waitingDays} confirmedAt={cur.confirm?.at ?? null} documentNo={cur.documentNo} deadline={cur.deadline} />,
    help: [
      // #276：見出しのチップ（番号ありの下書き）の title と同じ1行
      (cur.state === "unissued" || cur.state === "draft") && cur.documentNo ? `${unissuedTitle(cur.documentNo)}。` : null,
      t("明細の金額は{vendor}への支払いそのものです（社内用の列はありません）。"),
      t(PAYMENT_NOTICE_MSG.issueNote).replace("{days}", String(cur.confirmDays)),
      phone ? t("スマホでは閲覧のみです。修正はパソコンで行います。") : null,
    ],
    head: ({ total }) => (
      <>
        <div className="paper pnterms" data-testid="notice-terms">
          <span data-testid="notice-vendor-tax">{noticeVendorText(cur)}</span>
          <span>
            <span className="muted">{PAYMENT_NOTICE_MSG.settingsConfirmDays}</span> {`送付後 ${cur.confirmDays}日`}
          </span>
          {cur.deadline ? (
            <span>
              <span className="muted">{PAYMENT_NOTICE_MSG.confirmDeadline}</span> <span className="num">{cur.deadline.replaceAll("-", "/")}</span>
            </span>
          ) : null}
        </div>
        <ErrorText>{hq.state.kind === "error" ? hq.state.msg : undefined}</ErrorText>
      </>
    ),
    footer: (
      <>
        <div className="paper memo">
          <div className="field">
            <label htmlFor="pn_memo">備考（紙の下に印字されます）</label>
            <textarea id="pn_memo" value={head.notes} readOnly={readOnly} onChange={(e) => setH({ notes: e.target.value })} />
          </div>
        </div>
      </>
    ),
  };

  return (
    <>
      <Editor initial={initial} invoice={backend} />
      {hq.dialog}
      {dlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !dlg.busy && setDlg(null)}>
          <div className="dlg" role="dialog" aria-label={PAYMENT_NOTICE_MSG.issueTitle} onKeyDown={(e) => e.key === "Escape" && !dlg.busy && setDlg(null)} data-testid="notice-issue-dialog">
            <div className="dh">{PAYMENT_NOTICE_MSG.issueTitle}</div>
            <div className="db">
              <p className="muted" style={{ margin: 0 }}>
                {title}　{cur.client.name} {cur.client.honorific || "御中"}　合計 {yen(cur.revision.total)}円
              </p>
              {/* #278：番号のある下書き（発行を取消した）は、同じ番号で再発行することを本文の1行で */}
              {cur.documentNo ? <p style={{ margin: 0 }} data-testid="issue-reissue">{unissuedReissue(cur.documentNo)}</p> : null}
              <p style={{ margin: 0 }} data-testid="notice-issue-vendor-tax">{noticeVendorText(cur)}</p>
              <p className="hint" style={{ margin: 0 }}>{t(PAYMENT_NOTICE_MSG.issueNote).replace("{days}", String(cur.confirmDays))}</p>
              {needsCompany ? (
                <Field id="pni_co" label="会社名（屋号・お名前でも）">
                  <input id="pni_co" className="inp" autoComplete="organization" value={dlg.company} onChange={(e) => setDlg({ ...dlg, company: e.target.value, error: undefined })} />
                </Field>
              ) : null}
              <ErrorText>{dlg.error}</ErrorText>
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn quiet" disabled={dlg.busy} onClick={() => setDlg(null)}>取消</button>
              <button type="button" className="btn primary" disabled={dlg.busy} onClick={() => void doIssue()} data-testid="notice-issue-confirm">
                {PAYMENT_NOTICE_MSG.issueConfirm}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {askDelete ? <DeleteDraftDialog prefix="notice" onClose={() => setAskDelete(false)} onConfirm={deleteDraft} /> : null}
      {unissueDlg ? <UnissueDialog doc="payment_notice" no={initial.displayNo ?? initial.documentNo ?? ""} busy={unissueDlg.busy} error={unissueDlg.error} onClose={() => setUnissueDlg(null)} onConfirm={() => void doUnissue()} /> : null}
      {toastNode}
    </>
  );
}
